import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

// Support both camelCase and snake_case (e.g. from API)
const getProfileValue = (profile, ...keys) => {
  if (!profile) return undefined;
  for (const k of keys) {
    const v = profile[k];
    if (v !== undefined) return v;
  }
  return undefined;
};

const ProfileCompletionMeter = ({ 
  completionPercentage = 0,
  missingSections = [],
  onEditProfile,
  userProfile = null
}) => {
  const getCompletionColor = () => {
    if (completionPercentage >= 80) return 'text-success';
    if (completionPercentage >= 50) return 'text-warning';
    return 'text-error';
  };

  const getCompletionBgColor = () => {
    if (completionPercentage >= 80) return 'bg-success';
    if (completionPercentage >= 50) return 'bg-warning';
    return 'bg-error';
  };

  // Dynamic checklist from user profile (camelCase or snake_case)
  const photos = getProfileValue(userProfile, 'photos');
  const primaryPhoto = getProfileValue(userProfile, 'primaryPhoto', 'primary_photo');
  const bio = (getProfileValue(userProfile, 'bio') || '').trim();
  const interests = getProfileValue(userProfile, 'interests');
  const city = (getProfileValue(userProfile, 'city') || '').trim();
  const hasPhotos = Boolean(
    (Array.isArray(photos) && photos.length > 0) || primaryPhoto
  );
  const hasBio = Boolean(bio);
  const hasInterests = Array.isArray(interests) && interests.length > 0;
  const hasLocation = Boolean(city);

  const suggestions = [
    { id: 1, icon: 'Camera', text: 'Add profile photos', completed: hasPhotos },
    { id: 2, icon: 'FileText', text: 'Complete bio section', completed: hasBio },
    { id: 3, icon: 'Heart', text: 'Add interests', completed: hasInterests },
    { id: 4, icon: 'MapPin', text: 'Verify location', completed: hasLocation }
  ];

  return (
    <div className="bg-card rounded-xl p-4 md:p-6 shadow-warm">
      <div className="flex items-start justify-between mb-4 md:mb-6">
        <div>
          <h2 className="text-lg md:text-xl font-heading font-semibold mb-1">Profile Completion</h2>
          <p className="text-xs md:text-sm text-muted-foreground">
            Complete your profile to increase match potential
          </p>
        </div>
        <div className={`text-2xl md:text-3xl font-heading font-bold ${getCompletionColor()}`}>
          {completionPercentage}%
        </div>
      </div>
      <div className="relative w-full h-3 md:h-4 bg-muted rounded-full overflow-hidden mb-4 md:mb-6">
        <div
          className={`absolute inset-y-0 left-0 ${getCompletionBgColor()} transition-all duration-500 rounded-full`}
          style={{ width: `${completionPercentage}%` }}
        />
      </div>
      <div className="space-y-2 md:space-y-3 mb-4 md:mb-6">
        {suggestions?.map((suggestion) => (
          <div
            key={suggestion?.id}
            className={`flex items-center gap-3 p-3 rounded-lg transition-smooth ${
              suggestion?.completed ? 'bg-success/10' : 'bg-muted hover:bg-muted/80'
            }`}
          >
            <div className={`flex-shrink-0 ${suggestion?.completed ? 'text-success' : 'text-muted-foreground'}`}>
              <Icon name={suggestion?.completed ? 'CheckCircle2' : suggestion?.icon} size={20} />
            </div>
            <span className={`text-sm md:text-base flex-1 ${suggestion?.completed ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
              {suggestion?.text}
            </span>
          </div>
        ))}
      </div>
      <Button
        variant="default"
        fullWidth
        iconName="Edit"
        iconPosition="left"
        onClick={onEditProfile}
      >
        Complete Profile
      </Button>
    </div>
  );
};

export default ProfileCompletionMeter;